import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import API from '../utils/api';
import { trackGiftSuccess } from '../utils/analytics';

const PLAN_LABELS = { basic: 'Basic', family: 'Family' };

// ── Subscription gift success ─────────────────────────────
const SubscriptionSuccess = ({ sessionId }) => {
  const [status, setStatus] = useState('loading');
  const [data, setData]     = useState(null);
  const pollRef             = useRef(null);

  useEffect(() => {
    if (!sessionId) { setStatus('invalid'); return; }

    const poll = async () => {
      try {
        const res = await API.get(`/gift/subscription/session/${sessionId}`);
        const d = res.data;
        setData(d);
        if (d.status === 'sent' || d.status === 'redeemed') {
          setStatus('sent');
          clearInterval(pollRef.current);
          trackGiftSuccess(d.plan || 'subscription');
        } else if (d.status === 'failed') {
          setStatus('failed');
          clearInterval(pollRef.current);
        } else {
          setStatus('processing');
        }
      } catch {
        clearInterval(pollRef.current);
        setStatus('invalid');
      }
    };

    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [sessionId]);

  if (status === 'loading' || status === 'processing') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
        <div className="text-center text-white px-4" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎁</div>
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive" }}>Sending your gift…</h2>
          <p style={{ color: '#c9a8ff', fontSize: '16px', marginBottom: '32px' }}>
            Payment confirmed! We're generating the claim link and sending it to {data?.recipientEmail || 'the recipient'}.
          </p>
          <div className="d-flex justify-content-center mb-4">
            <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: 'var(--text-main)' }}></div>
          </div>
          <p style={{ color: '#c9a8ff', fontSize: '13px' }}>This usually takes just a moment.</p>
        </div>
      </div>
    );
  }

  if (status === 'sent') {
    const planLabel = PLAN_LABELS[data?.plan] || 'Basic';
    const months    = data?.durationMonths || 1;
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'var(--page-bg)' }}>
        <div className="container" style={{ maxWidth: '560px' }}>
          <div className="card border-0 shadow" style={{ borderRadius: '24px', overflow: 'hidden' }}>
            <div className="p-5 text-center" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
              <div style={{ fontSize: '64px', marginBottom: '12px' }}>🎉</div>
              <h2 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>Gift sent!</h2>
              <p style={{ color: '#c9a8ff', fontSize: '16px', margin: 0 }}>
                Claim link delivered to {data?.recipientEmail}
              </p>
            </div>
            <div className="p-5">
              <div className="text-center p-4 rounded-3 mb-4" style={{ background: 'var(--purple-section)', border: '2px dashed #c9a8ff' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🌟</div>
                <p className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '18px' }}>
                  {planLabel} — {months} Month{months > 1 ? 's' : ''}
                </p>
                <p className="text-muted small mb-0">They'll receive a link to claim their subscription</p>
              </div>
              <div className="d-flex flex-column gap-3 mb-4">
                {[
                  ['bi-envelope-check-fill', 'Claim link emailed to recipient'],
                  ['bi-person-check-fill',   'Confirmation sent to your email'],
                  ['bi-clock-history',       'Link is valid for 90 days'],
                ].map(([icon, text]) => (
                  <div key={text} className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: '36px', height: '36px', minWidth: '36px', background: 'var(--purple-section)' }}>
                      <i className={`bi ${icon}`} style={{ color: '#6f42c1' }}></i>
                    </div>
                    <span className="small" style={{ color: 'var(--text-main)' }}>{text}</span>
                  </div>
                ))}
              </div>
              <div className="d-grid gap-2">
                <Link to="/gift?type=subscription" className="btn btn-dream rounded-pill fw-semibold">
                  <i className="bi bi-gift-fill me-2"></i>Send Another Gift
                </Link>
                <Link to="/pricing" className="btn btn-outline-secondary rounded-pill">
                  Get your own subscription
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'var(--page-bg)' }}>
        <div className="text-center px-4" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>😔</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Something went wrong</h4>
          <p className="text-muted mb-4">Your payment was successful but we couldn't send the gift. Our team has been notified.</p>
          <a href="mailto:support@dreamybedtime.com" className="btn btn-dream rounded-pill px-4">Contact Support</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
      <div className="text-center text-white px-4">
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>🌙</div>
        <h4 className="fw-bold mb-2">Page not found</h4>
        <p style={{ color: '#c9a8ff', marginBottom: '24px' }}>This link doesn't look right.</p>
        <Link to="/gift" className="btn btn-light rounded-pill px-4">Gift a Subscription</Link>
      </div>
    </div>
  );
};

// ── Story gift (original flow) ────────────────────────────
const StorySuccess = ({ sessionId }) => {
  const [status, setStatus] = useState('loading'); // loading | generating | delivered | failed | invalid
  const [childName, setChildName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setStatus('invalid'); return; }

    const poll = async () => {
      try {
        const res = await API.get(`/gift/status/${sessionId}`);
        const { status: s, childName: cn, recipientEmail: re, storyTitle: st } = res.data;
        if (cn) setChildName(cn);
        if (re) setRecipientEmail(re);
        if (st) setStoryTitle(st);
        setStatus(s === 'pending' ? 'loading' : s);

        if (s === 'delivered' || s === 'failed') {
          clearInterval(pollRef.current);
        }
      } catch {
        clearInterval(pollRef.current);
        setStatus('invalid');
      }
    };

    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [sessionId]);

  // ── Generating / loading ──────────────────────────────
  if (status === 'loading' || status === 'generating') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
        <div className="text-center text-white px-4" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>✨</div>
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive" }}>
            Crafting the story…
          </h2>
          <p style={{ color: '#c9a8ff', fontSize: '16px', marginBottom: '32px' }}>
            Payment confirmed! We're weaving a magical, personalised bedtime story
            {childName ? ` for ${childName}` : ''}. This usually takes under a minute.
          </p>
          <div className="d-flex justify-content-center mb-4">
            <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: 'var(--text-main)' }}></div>
          </div>
          <div className="progress" style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.2)' }}>
            <div className="progress-bar progress-bar-striped progress-bar-animated w-75"
              style={{ background: 'var(--purple-section)' }}></div>
          </div>
          <p className="mt-3" style={{ color: '#c9a8ff', fontSize: '13px' }}>
            The story will be delivered to {recipientEmail || 'the recipient'} automatically.
          </p>
        </div>
      </div>
    );
  }

  // ── Delivered ─────────────────────────────────────────
  if (status === 'delivered') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'var(--page-bg)' }}>
        <div className="container" style={{ maxWidth: '560px' }}>
          <div className="card border-0 shadow" style={{ borderRadius: '24px', overflow: 'hidden' }}>
            {/* Header */}
            <div className="p-5 text-center" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
              <div style={{ fontSize: '64px', marginBottom: '12px' }}>🎉</div>
              <h2 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
                Gift delivered!
              </h2>
              <p style={{ color: '#c9a8ff', fontSize: '16px', margin: 0 }}>
                The story is on its way to {recipientEmail}
              </p>
            </div>

            {/* Body */}
            <div className="p-5">
              {storyTitle && (
                <div className="text-center p-4 rounded-3 mb-4"
                  style={{ background: 'var(--purple-section)', border: '2px dashed #c9a8ff' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>🌙</div>
                  <p className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '18px' }}>
                    "{storyTitle}"
                  </p>
                  <p className="text-muted small mb-0">A bedtime story for {childName}</p>
                </div>
              )}

              <div className="d-flex flex-column gap-3 mb-4">
                {[
                  ['bi-envelope-check-fill', '#2d1b69', `Story emailed to ${recipientEmail}`],
                  ['bi-person-check-fill',   '#2d1b69', `Confirmation sent to your email`],
                  ['bi-stars',               '#6f42c1', `${childName} stars in their own adventure`],
                ].map(([icon, color, text]) => (
                  <div key={text} className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: '36px', height: '36px', minWidth: '36px', background: 'var(--purple-section)' }}>
                      <i className={`bi ${icon}`} style={{ color }}></i>
                    </div>
                    <span className="small" style={{ color: 'var(--text-main)' }}>{text}</span>
                  </div>
                ))}
              </div>

              <div className="d-grid gap-2">
                <Link to="/gift" className="btn btn-dream rounded-pill fw-semibold">
                  <i className="bi bi-gift-fill me-2"></i>Send Another Gift Story
                </Link>
                <Link to="/register" className="btn btn-outline-secondary rounded-pill">
                  Create Your Own Stories Free
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-muted small mt-4">
            Didn't receive your confirmation? Check your spam folder or{' '}
            <a href="mailto:support@dreamybedtime.com" style={{ color: '#6f42c1' }}>contact support</a>.
          </p>
        </div>
      </div>
    );
  }

  // ── Failed ─────────────────────────────────────────────
  if (status === 'failed') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'var(--page-bg)' }}>
        <div className="text-center px-4" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>😔</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Something went wrong</h4>
          <p className="text-muted mb-4">
            Your payment was successful but we hit a snag generating the story.
            Our team has been notified and will resolve this shortly.
          </p>
          <a href="mailto:support@dreamybedtime.com" className="btn btn-dream rounded-pill px-4 me-2">
            Contact Support
          </a>
          <Link to="/gift" className="btn btn-outline-secondary rounded-pill px-4">Try Again</Link>
        </div>
      </div>
    );
  }

  // ── Invalid / not found ───────────────────────────────
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
      <div className="text-center text-white px-4">
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>🌙</div>
        <h4 className="fw-bold mb-2">Page not found</h4>
        <p style={{ color: '#c9a8ff', marginBottom: '24px' }}>This link doesn't look right.</p>
        <Link to="/gift" className="btn btn-light rounded-pill px-4">Gift a Story</Link>
      </div>
    </div>
  );
};

const GiftSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const giftType  = searchParams.get('type');

  if (giftType === 'subscription') {
    return <SubscriptionSuccess sessionId={sessionId} />;
  }
  return <StorySuccess sessionId={sessionId} />;
};

export default GiftSuccessPage;
